import { useQuery } from "@tanstack/react-query";
import { deliverableFormatLabel, rateText, revisionRateText } from "@talentkontor/domain";
import { api } from "../../data";
import { money } from "../../lib/format";
import { dateOf, useT } from "./sprache";

/** Preisliste, wie die Agentur sie für den Creator freigibt (0072, public.talent_rate_card); ohne Freigabe nichts */
export interface MeinePreise {
  currency: string;
  usage_bp: number | null;
  usage_per_month: boolean;
  exclusivity_bp: number | null;
  exclusivity_per_month: boolean;
  rush_bp: number | null;
  revision_bp: number | null;
  note: string | null;
  updated_at: string;
  items: { platform: string; format: string; format_label: string | null; price_cents: number; platform_label_de: string | null; platform_label_en: string | null }[];
}

export function ladeMeinePreise(talentId: string): Promise<MeinePreise | null> {
  return api.read<MeinePreise | null>("talent_rate_card", { p_talent: talentId });
}

/**
 * Deine Preisliste (Creator-Portal, DE/EN): Grundpreise je Plattform und Format und die Zuschläge. Erscheint nur, wenn die
 * Agentur die Preisliste freigibt (Einstellungen · Konditionen · Angebote). Anfragen und Angebote selbst sieht der Creator nie.
 */
export function MeinePreisliste({ talentId, lang, timeZone }: { talentId: string; lang: "de" | "en"; timeZone: string }) {
  const t = useT();
  const q = useQuery({ queryKey: ["talent_rate_cards", "mine", talentId], queryFn: () => ladeMeinePreise(talentId) });
  if (!q.data) return null;
  const p = q.data;
  const label = (x: MeinePreise["items"][number]) => (lang === "en" ? x.platform_label_en : x.platform_label_de) ?? x.platform;
  return (
    <section className="panel stack" aria-labelledby="meine-preise">
      <h2 id="meine-preise">{t("Deine Preisliste", "Your rate card")}</h2>
      <p>{t("So bietet deine Agentur dich Marken an. Fragen dazu klärst du am besten im Chat mit deiner Agentur.",
        "This is how your agency quotes you to brands. For questions, message your agency in the chat.")}</p>
      {p.items.length > 0 ? (
        <div className="tablewrap tk-cardtable tk-cardnarrow">
          <table>
            <caption className="sr-only">{t("Grundpreise", "Base prices")}</caption>
            <thead><tr><th scope="col">{t("Plattform", "Platform")}</th><th scope="col">{t("Format", "Format")}</th><th scope="col" className="money">{t("Grundpreis", "Base price")}</th></tr></thead>
            <tbody>
              {p.items.map((x, i) => (
                <tr key={i}>
                  <td data-label={t("Plattform", "Platform")}>{label(x)}</td>
                  <td data-label={t("Format", "Format")}>{deliverableFormatLabel(x.format, x.format_label, lang)}</td>
                  <td data-label={t("Grundpreis", "Base price")} className="num">{money(x.price_cents, p.currency)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : <p className="muted">{t("Noch keine Preise hinterlegt.", "No prices yet.")}</p>}
      <div className="task"><span>{t("Nutzungsrechte (Paid Social)", "Usage rights (paid social)")}</span><span>{rateText(p.usage_bp, p.usage_per_month, lang)}</span></div>
      <div className="task"><span>{t("Exklusivität", "Exclusivity")}</span><span>{rateText(p.exclusivity_bp, p.exclusivity_per_month, lang)}</span></div>
      <div className="task"><span>{t("Eilzuschlag", "Rush fee")}</span><span>{rateText(p.rush_bp, false, lang)}</span></div>
      <div className="task"><span>{t("Zusätzliche Korrekturschleife", "Additional revision round")}</span><span>{revisionRateText(p.revision_bp, lang)}</span></div>
      {p.note && <p className="source-note">{p.note}</p>}
      <small className="muted">{t(`Zuschläge in Prozent auf den Grundpreis. Stand ${dateOf(p.updated_at, lang, timeZone)}.`, `Surcharges in percent of the base price. As of ${dateOf(p.updated_at, lang, timeZone)}.`)}</small>
    </section>
  );
}
